import React, { useState, useRef, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  PORTAL_LOGO_DISPLAY_PX,
  PORTAL_LOGO_EXPORT_PX,
  PORTAL_LOGO_MAX_UPLOAD_BYTES,
  PORTAL_LOGO_ACCEPT,
  PORTAL_LOGO_DEFAULT_SHAPE,
} from '../constants/portalLogo';
import { cropImageToSquareBlob, loadImageFromFile } from '../utils/cropImageToSquare';
import { uploadPortalLogo, removePortalLogo } from '../services/schoolPublicProfile';

const PREVIEW_PX = 220;
const PANEL_WIDTH = 420;

const PortalSchoolLogo = ({
  schoolId,
  portalLogo,
  labels = {},
  onLogoChange,
  onSyncStatus,
}) => {
  const fileInputRef = useRef(null);
  const panelRef = useRef(null);
  const [cropOpen, setCropOpen] = useState(false);
  const [sourceImage, setSourceImage] = useState(null);
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [previewUrl, setPreviewUrl] = useState(null);
  const [busy, setBusy] = useState(false);
  const [imageDragging, setImageDragging] = useState(false);
  const imageDragStart = useRef({ x: 0, y: 0, ox: 0, oy: 0 });
  const [panelPos, setPanelPos] = useState({ x: 24, y: 24 });
  const [panelDragging, setPanelDragging] = useState(false);
  const panelDragStart = useRef({ x: 0, y: 0, px: 0, py: 0 });

  const shape = portalLogo?.shape || PORTAL_LOGO_DEFAULT_SHAPE;
  const hasLogo = !!portalLogo?.publicUrl;

  const centerPanel = useCallback(() => {
    const el = panelRef.current;
    const w = el?.offsetWidth || PANEL_WIDTH;
    const h = el?.offsetHeight || 480;
    setPanelPos({
      x: Math.max(12, (window.innerWidth - w) / 2),
      y: Math.max(12, (window.innerHeight - h) / 2),
    });
  }, []);

  useEffect(() => {
    if (!cropOpen) return undefined;
    const t = requestAnimationFrame(() => centerPanel());
    const onResize = () => centerPanel();
    window.addEventListener('resize', onResize);
    return () => {
      cancelAnimationFrame(t);
      window.removeEventListener('resize', onResize);
    };
  }, [cropOpen, centerPanel]);

  useEffect(() => {
    if (!panelDragging) return undefined;
    const onMove = (e) => {
      const clientX = e.touches ? e.touches[0]?.clientX : e.clientX;
      const clientY = e.touches ? e.touches[0]?.clientY : e.clientY;
      if (clientX == null || clientY == null) return;
      const dx = clientX - panelDragStart.current.x;
      const dy = clientY - panelDragStart.current.y;
      const w = panelRef.current?.offsetWidth || PANEL_WIDTH;
      const h = panelRef.current?.offsetHeight || 480;
      setPanelPos({
        x: Math.min(Math.max(8, panelDragStart.current.px + dx), window.innerWidth - w - 8),
        y: Math.min(Math.max(8, panelDragStart.current.py + dy), window.innerHeight - h - 8),
      });
    };
    const onUp = () => setPanelDragging(false);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('touchend', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onUp);
    };
  }, [panelDragging]);

  const refreshPreview = useCallback(async () => {
    if (!sourceImage) {
      setPreviewUrl(null);
      return;
    }
    try {
      const blob = await cropImageToSquareBlob({
        image: sourceImage,
        size: PREVIEW_PX,
        scale,
        offsetX: offset.x,
        offsetY: offset.y,
      });
      const url = URL.createObjectURL(blob);
      setPreviewUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return url;
      });
    } catch (e) {
      console.error(e);
    }
  }, [sourceImage, scale, offset]);

  useEffect(() => {
    refreshPreview();
  }, [refreshPreview]);

  useEffect(() => () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
  }, [previewUrl]);

  const openFilePicker = () => fileInputRef.current?.click();

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > PORTAL_LOGO_MAX_UPLOAD_BYTES) {
      onSyncStatus?.(labels.logo_error_too_large || 'Image is too large (max 2 MB).');
      return;
    }
    try {
      const img = await loadImageFromFile(file);
      setSourceImage(img);
      setScale(1);
      setOffset({ x: 0, y: 0 });
      setCropOpen(true);
    } catch (err) {
      onSyncStatus?.(labels.logo_error_load || 'Could not load that image.');
    }
  };

  const handleImagePanStart = (clientX, clientY) => {
    if (!sourceImage || panelDragging) return;
    setImageDragging(true);
    imageDragStart.current = { x: clientX, y: clientY, ox: offset.x, oy: offset.y };
  };

  const handleImagePanMove = (clientX, clientY) => {
    if (!imageDragging) return;
    setOffset({
      x: imageDragStart.current.ox + (clientX - imageDragStart.current.x),
      y: imageDragStart.current.oy + (clientY - imageDragStart.current.y),
    });
  };

  const handleImagePanEnd = () => setImageDragging(false);

  const handlePanelDragStart = (e) => {
    if (e.button !== 0 && e.type !== 'touchstart') return;
    e.preventDefault();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    setPanelDragging(true);
    panelDragStart.current = { x: clientX, y: clientY, px: panelPos.x, py: panelPos.y };
  };

  const handleSaveCrop = async () => {
    if (!sourceImage || !schoolId) return;
    setBusy(true);
    onSyncStatus?.(labels.logo_uploading || 'Uploading school logo…');
    try {
      const blob = await cropImageToSquareBlob({
        image: sourceImage,
        size: PORTAL_LOGO_EXPORT_PX,
        scale,
        offsetX: offset.x,
        offsetY: offset.y,
      });
      const next = await uploadPortalLogo(schoolId, blob, shape);
      onLogoChange?.(next);
      setCropOpen(false);
      setSourceImage(null);
      onSyncStatus?.(labels.logo_saved || 'School logo saved.');
    } catch (err) {
      console.error(err);
      onSyncStatus?.(labels.logo_error_upload || 'Logo upload failed.');
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = async () => {
    if (!schoolId || !hasLogo) return;
    if (!window.confirm(labels.logo_remove_confirm || 'Remove your school logo?')) return;
    setBusy(true);
    try {
      await removePortalLogo(schoolId, portalLogo);
      onLogoChange?.(null);
      onSyncStatus?.(labels.logo_removed || 'School logo removed.');
    } catch (err) {
      console.error(err);
      onSyncStatus?.(labels.logo_error_remove || 'Could not remove logo.');
    } finally {
      setBusy(false);
    }
  };

  const maskClass =
    shape === 'square'
      ? 'portal-school-logo__frame--square'
      : 'portal-school-logo__frame--circle';

  const cropModal = cropOpen && sourceImage ? (
    <div
      className="portal-logo-crop-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="portal-logo-crop-title"
    >
      <div className="portal-logo-crop-modal__backdrop" onClick={() => !busy && setCropOpen(false)} />
      <div
        ref={panelRef}
        className="portal-logo-crop-modal__panel"
        style={{ left: panelPos.x, top: panelPos.y }}
      >
        <div
          className="portal-logo-crop-modal__drag-bar"
          onMouseDown={handlePanelDragStart}
          onTouchStart={handlePanelDragStart}
          role="presentation"
        >
          <span className="portal-logo-crop-modal__drag-grip" aria-hidden="true" />
          <h2 id="portal-logo-crop-title" className="portal-logo-crop-modal__title">
            {labels.logo_crop_title || 'Adjust your school logo'}
          </h2>
          <span className="portal-logo-crop-modal__drag-hint">
            {labels.logo_crop_drag_panel || 'Drag here to move'}
          </span>
        </div>
        <p className="portal-logo-crop-modal__hint">
          {labels.logo_crop_hint ||
            'Drag the image to reposition and use the slider to zoom. Your logo will fit the same size as the IFIP logo in the header.'}
        </p>

        <div
          className={`portal-logo-crop-modal__preview ${maskClass}`}
          onMouseDown={(e) => {
            e.stopPropagation();
            handleImagePanStart(e.clientX, e.clientY);
          }}
          onMouseMove={(e) => handleImagePanMove(e.clientX, e.clientY)}
          onMouseUp={handleImagePanEnd}
          onMouseLeave={handleImagePanEnd}
          onTouchStart={(e) => {
            e.stopPropagation();
            const t = e.touches[0];
            if (t) handleImagePanStart(t.clientX, t.clientY);
          }}
          onTouchMove={(e) => {
            const t = e.touches[0];
            if (t) handleImagePanMove(t.clientX, t.clientY);
          }}
          onTouchEnd={handleImagePanEnd}
        >
          {previewUrl ? (
            <img src={previewUrl} alt="" className="portal-logo-crop-modal__preview-img" draggable={false} />
          ) : null}
        </div>

        <label className="portal-logo-crop-modal__zoom-label" htmlFor="portal-logo-zoom">
          {labels.logo_crop_zoom || 'Zoom'}
        </label>
        <input
          id="portal-logo-zoom"
          type="range"
          min="1"
          max="3"
          step="0.02"
          value={scale}
          onChange={(e) => setScale(parseFloat(e.target.value))}
          className="portal-logo-crop-modal__zoom"
        />

        <div className="portal-logo-crop-modal__actions">
          <button type="button" className="btn-secondary" onClick={() => setCropOpen(false)} disabled={busy}>
            {labels.logo_crop_cancel || 'Cancel'}
          </button>
          <button type="button" className="btn-primary-action" onClick={handleSaveCrop} disabled={busy}>
            {labels.logo_crop_save || 'Save logo'}
          </button>
        </div>
      </div>
    </div>
  ) : null;

  return (
    <>
      <div className="portal-school-logo">
        <button
          type="button"
          className={`portal-school-logo__trigger ${maskClass}${hasLogo ? '' : ' portal-school-logo__trigger--empty'}`}
          onClick={openFilePicker}
          disabled={busy}
          title={hasLogo ? labels.logo_change_hint : labels.logo_upload_cta}
          aria-label={hasLogo ? labels.logo_change_hint : labels.logo_upload_cta}
        >
          {hasLogo ? (
            <img
              src={portalLogo.publicUrl}
              alt={labels.logo_alt || 'School logo'}
              className="portal-school-logo__img"
              width={PORTAL_LOGO_DISPLAY_PX}
              height={PORTAL_LOGO_DISPLAY_PX}
            />
          ) : (
            <span className="portal-school-logo__placeholder">
              {labels.logo_upload_cta || 'Click here to upload your school logo'}
            </span>
          )}
        </button>
        {hasLogo ? (
          <button
            type="button"
            className="portal-school-logo__remove"
            onClick={handleRemove}
            disabled={busy}
          >
            {labels.logo_remove || 'Remove'}
          </button>
        ) : null}
        <input
          ref={fileInputRef}
          type="file"
          accept={PORTAL_LOGO_ACCEPT}
          className="portal-school-logo__file-input"
          onChange={handleFile}
          tabIndex={-1}
          aria-hidden
        />
      </div>
      {cropModal && typeof document !== 'undefined' ? createPortal(cropModal, document.body) : null}
    </>
  );
};

export default PortalSchoolLogo;
